import { translateBackend } from '@assets/i18n/config';
import type { TranslatedString } from '@interfaces/shared';
import { ConfirmationModal } from '@ovComponents/3-pattern/confirmationDialog/confirmationDialog';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { Alert, Typography } from 'hephaestus/web';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export type DetachModelPortfolioOptions = {
  title?: TranslatedString;
  subtitle?: TranslatedString;
  detachWarningEnable?: boolean;
  detachWarningText?: TranslatedString;
  detachButton?: TranslatedString;
};

export type DetachModelPortfolioVisualProps = {
  options: DetachModelPortfolioOptions;
  loading?: boolean;
  onNext: () => void | Promise<void>;
};

export const DetachModelPortfolioVisual = ({
  options,
  loading = false,
  onNext,
}: DetachModelPortfolioVisualProps) => {
  const { t } = useTranslation(['workflowCompletions', 'components']);
  const [confirming, setConfirming] = useState(false);

  const warningText = translateBackend(options?.detachWarningText);
  // The warning defaults on, matching the sub-step option default, so an
  // unconfigured step still warns before it detaches.
  const showWarning = options?.detachWarningEnable !== false && !!warningText;
  const detachLabel =
    translateBackend(options?.detachButton) ||
    t('workflowCompletions:householdDetach.detachButton');

  /**
   * Detaching is destructive and immediate, so the step's own button only opens
   * the confirmation. Closing afterwards matters on failure: on success the
   * workflow has already advanced past this step.
   */
  const confirmDetach = async () => {
    await onNext();
    setConfirming(false);
  };

  return (
    <WorkflowCompletionForm
      onSubmit={() => setConfirming(true)}
      testId='detach-model-portfolio-form'
      submitButtonProps={{
        label: detachLabel,
        color: 'error',
        disabled: loading,
        dataTestId: 'detach-model-portfolio-detach-button',
      }}
    >
      <Typography variant='displayLarge' sx={{ mt: 1 }}>
        {translateBackend(options?.title)}
      </Typography>
      {options?.subtitle && (
        <Typography variant='bodyLarge' sx={{ mb: 3 }}>
          <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
            {translateBackend(options?.subtitle)}
          </ReactMarkdown>
        </Typography>
      )}
      {showWarning && (
        <Alert severity='warning' data-testid='detach-model-portfolio-warning'>
          <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
            {warningText}
          </ReactMarkdown>
        </Alert>
      )}
      <ConfirmationModal
        open={confirming}
        testIdPrefix='detach-model-portfolio-confirmation'
        title={t('components:householdPortfolio.detachModelTitle')}
        bodyText={t('components:householdPortfolio.detachModelBody')}
        confirmButtonLabel={detachLabel}
        loading={loading}
        onConfirm={confirmDetach}
        onCancel={() => setConfirming(false)}
      />
    </WorkflowCompletionForm>
  );
};

export default DetachModelPortfolioVisual;
